import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Warehouse,
  Building2,
  Truck,
  HardHat,
  ClipboardList,
  Bell,
  Landmark,
  Users,
  Wallet,
  Settings,
  BookOpen,
  Receipt,
  TrendingUp,
  TrendingDown,
  BarChart3,
  type LucideIcon,
} from 'lucide-react';
import { useAlertasCount } from '@/hooks/useAlertasCount';
import { Logo } from '@/components/ui/Logo';
import { cn } from '@/lib/cn';

type Item = { to: string; label: string; icon: LucideIcon; disabled?: boolean; badge?: string };
type Group = { title: string; items: Item[] };

const groups: Group[] = [
  {
    title: 'General',
    items: [
      { to: '/app', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    title: 'Recursos Financieros',
    items: [
      { to: '/app/presupuesto/egresos', label: 'Presupuesto egresos', icon: TrendingUp },
      { to: '/app/contabilidad/polizas', label: 'Pólizas',           icon: BookOpen },
      { to: '/app/cxp',                  label: 'Cuentas por Pagar',  icon: Receipt },
      { to: '/app/bancos',               label: 'Bancos',             icon: Landmark },
      { to: '#tes',  label: 'Tesorería',          icon: Wallet, disabled: true },
      { to: '#fin',  label: 'Conciliación',       icon: Wallet, disabled: true },
    ],
  },
  {
    title: 'Recursos Materiales',
    items: [
      { to: '/app/proveedores',  label: 'Proveedores',     icon: Truck },
      { to: '/app/articulos',    label: 'Artículos',       icon: Package },
      { to: '/app/almacen',      label: 'Almacén',         icon: Warehouse },
      { to: '/app/conteos',      label: 'Toma física',     icon: Warehouse },
      { to: '/app/bienes',       label: 'Bienes',          icon: Building2 },
      { to: '/app/depreciacion', label: 'Depreciación',    icon: TrendingDown },
      { to: '/app/patrimonio',   label: 'Patrimonio',      icon: BarChart3 },
      { to: '/app/requisiciones', label: 'Requisiciones',  icon: ClipboardList },
      { to: '/app/ordenes-compra',label: 'Órdenes compra', icon: Receipt },
      { to: '/app/cotizaciones', label: 'Cotizaciones',   icon: ClipboardList },
      { to: '/app/contratos',    label: 'Contratos',      icon: BookOpen },
      { to: '/app/licitaciones', label: 'Licitaciones',   icon: ClipboardList },
      { to: '/app/paa',          label: 'PAA',            icon: BarChart3 },
      { to: '/app/inversion',    label: 'Inversión púb.', icon: BarChart3 },
      { to: '/app/obras',        label: 'Obra pública',    icon: HardHat },
      { to: '/app/alertas',      label: 'Alertas',         icon: Bell, badge: 'alertas' },
    ],
  },
  {
    title: 'Recursos Humanos',
    items: [
      { to: '#rh',   label: 'Próximamente',     icon: Users, disabled: true },
    ],
  },
];

export function Sidebar() {
  const { data: alertasCount } = useAlertasCount();
  return (
    <aside className="flex h-full w-64 shrink-0 flex-col border-r border-border bg-surface">
      <div className="px-5 pb-4 pt-5">
        <Logo />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {groups.map((g) => (
          <div key={g.title} className="mt-4 first:mt-0">
            <h4 className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
              {g.title}
            </h4>
            <ul className="flex flex-col gap-0.5">
              {g.items.map((item) => {
                const Icon = item.icon;
                if (item.disabled) {
                  return (
                    <li key={item.to + item.label}>
                      <span className="flex cursor-not-allowed items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-ink-soft">
                        <Icon size={16} strokeWidth={1.75} />
                        <span className="flex-1 truncate">{item.label}</span>
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ink-mute">
                          Pronto
                        </span>
                      </span>
                    </li>
                  );
                }
                return (
                  <li key={item.to + item.label}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/app'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors',
                          isActive
                            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-100'
                            : 'text-ink-mute hover:bg-muted hover:text-ink'
                        )
                      }
                    >
                      <Icon size={16} strokeWidth={1.75} />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge === 'alertas' && alertasCount && alertasCount.total > 0 && (
                        <span className={cn(
                          'num inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                          (alertasCount.critica ?? 0) > 0
                            ? 'bg-rose-500 text-white'
                            : 'bg-amber-500 text-white'
                        )}>
                          {alertasCount.total}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <NavLink
          to="/app/configuracion"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium',
              isActive ? 'bg-muted text-ink' : 'text-ink-mute hover:bg-muted hover:text-ink'
            )
          }
        >
          <Settings size={16} strokeWidth={1.75} />
          Configuración
        </NavLink>
      </div>
    </aside>
  );
}
